import { getJSON } from 'common/services/api'
import { useQuery } from 'react-query'

type UseCarePlanPopulatedParams = {
  carePlanId: string
  enabled?: boolean
}

type PopulatedDocument = {
  id: string
  name: string
  createdAt: string
  pdf: string
}

type PopulatedRisk = {
  id: string
  name: string
  actions: string
  reviewedOn: string
  riskLevel?: string
  riskScore?: number
}

type PopulatedGoal = {
  id: string
  title: string
  status: string
  progress: number
  [key: string]: unknown
}

type PopulatedConsentForm = {
  id: string
  title: string
  isArchived: boolean
  createdOn: string
  consentStatus: string
}

export type ConsentFormSnapshot = {
  id: string
  title?: string
  body?: string
  isCustom?: boolean
  isArchived?: boolean
  isRefused?: boolean
  type?: string
  createdOn?: Date
  createdBy?: string
  editedOn?: Date
  editedBy?: string
  reviewComment?: string
  nextReviewDate?: Date
  consentTemplate?: string
  signature?: string
  signedBy?: string
  signedOn?: Date
  consentStatus?: string
  logs?: Array<{
    id: string
    title: string
    description: string
    category: {
      id: string
      icon: string
      color: string
    }
    item: {
      id: string
      icon: string
    }
    resident: string
    originalTimeLog?: string
    timeLogged: string
    isNextHandover?: boolean
    type?: string
    loggedBy: string
    witnessedBy?: string | null
    deleted?: boolean
    hidden?: boolean
    goals?: any[]
    notes?: any[]
  }>
}

type PopulatedSkillNeed = {
  id: string
  name: string
  homeGroup: string
  createdOn: string
  createdBy: string
  editedBy: string | null
}

type CarePlanPopulatedResponse = {
  data: {
    id: string
    resident: string
    seenBy: unknown[]
    editedBy: unknown | null
    isCustom: boolean
    isRevision?: boolean
    carePlanId: string
    logs: unknown[]
    risks: PopulatedRisk[]
    riskAssessments: unknown[]
    outcomes: string
    documents: PopulatedDocument[]
    skillNeeds: PopulatedSkillNeed[]
    goals: PopulatedGoal[]
    sections: unknown[]
    consentForms: PopulatedConsentForm[]
    consentFormsSnapshot?: ConsentFormSnapshot[]
    adl: {
      id: string
      name: string
      color: string
      icon: string
    }
    [key: string]: unknown
  }
}

export const useCarePlanPopulated = ({
  carePlanId,
  enabled = true
}: UseCarePlanPopulatedParams) => {
  const { data, isLoading, error, refetch } = useQuery(
    ['care-plans', carePlanId, 'populated'],
    () =>
      getJSON({
        url: `/api/list/care-plans/${carePlanId}/populated`
      }),
    { enabled: enabled && !!carePlanId }
  )

  return {
    data: mapCarePlanResponse(data as CarePlanPopulatedResponse),
    isLoading,
    error,
    refetch
  }
}

/**
 * Transforms populated risk data to the format expected by RiskAssessmentItem component.
 * The component expects riskAssessment.form.name, riskAssessment.formData.actions, etc.
 */
const transformRiskAssessment = (risk: PopulatedRisk) => ({
  id: risk.id,
  reviewedOn: risk.reviewedOn,
  createdOn: risk.reviewedOn, // Use reviewedOn as fallback for createdOn
  riskLevel: risk.riskLevel,
  riskScore: risk.riskScore,
  form: {
    name: risk.name,
    iconText: risk.name?.substring(0, 2)?.toUpperCase() || 'RA', // Generate icon text from name
    color: '#6D92EB', // Default color
    hasActions: !!risk.actions?.length
  },
  formData: {
    actions: risk.actions
  }
})

/**
 * Maps the populated API response to the format expected by CarePlan components.
 * The existing components expect:
 * - fields.documents, fields.riskAssessments, fields.goals as arrays of IDs
 * - Separate populated arrays for lookup (populatedDocuments, populatedRiskAssessments, etc.)
 */
const mapCarePlanResponse = (response: CarePlanPopulatedResponse) => {
  if (!response?.data) return null

  const { adl, documents, risks, goals, skillNeeds, consentForms, consentFormsSnapshot, ...rest } = response.data

  // Transform risks to match the expected structure for RiskAssessmentItem
  const transformedRisks = risks?.map(transformRiskAssessment) || []

  return {
    ...rest,
    // Map 'adl' to 'carePlanAdl' as expected by the components
    carePlanAdl: adl,
    // Keep original arrays of IDs for backwards compatibility with form state
    documents: documents?.map((doc) => doc.id) || [],
    riskAssessments: risks?.map((risk) => risk.id) || [],
    goals: goals?.map((goal) => goal.id) || [],
    skillNeeds: skillNeeds?.map((need) => need.id) || [],
    // Store the full populated data for components that need it
    populatedDocuments: documents || [],
    populatedRiskAssessments: transformedRisks,
    populatedGoals: goals || [],
    populatedSkillNeeds: skillNeeds || [],
    consentForms: consentForms || [],
    consentFormsSnapshot: consentFormsSnapshot || []
  }
}
